import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import Button from "../../../components/buttons/Button";
import MainTable from "../../../components/tables/MainTable";
import { ConfirmDeletePlanModal, DeleteSuccessModal } from "./DeletePlanModals";
import endpoints from "../../../services/endpoints";
import useApi from "../../../services/useApi";
import { TableDetailsItem } from "../../../components/tables/tableFunctions";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import usePrivilege from "../../../components/CustomHooks/usePrivilege";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
import { showToast } from "../../../components/toastMessage/toastSlice";
import { RootState } from "../../../store";
import { BundlePlanRow } from "./bundlePlanFields";

export default function ListOfBundlePlans() {
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { bundlePlans } = endpoints;
  const { DeleteAPI } = useApi();
  const { translate } = useTranslation(adminContents);
  const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
  // The listing response carries the MNO it belongs to, shown under the heading.
  const { entireResponse } = useSelector((state: RootState) => state.list);

  const [planToDelete, setPlanToDelete] = useState<BundlePlanRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDeletedModalOpen, setIsDeletedModalOpen] = useState(false);
  // How many remaining plans the server renumbered, from the delete response.
  const [resequencedCount, setResequencedCount] = useState(0);
  // Toggling this re-fetches the list after a plan is deleted.
  const [fetchListTrigger, setFetchListTrigger] = useState(false);

  const bundlePlanPrivileges = {
    list: usePrivilege("MM1"),
    edit: usePrivilege("MM2"),
    add: usePrivilege("MM4"),
  };

  // No min-w overrides here: the grid track widths come from tableMinWidth(id), so a
  // wider min-width on the cell would push it past its column and long values would
  // spill over the edge instead of truncating with an ellipsis.
  const TableDetails: { [key: string]: TableDetailsItem } = {
    "USSD Plan Code": {
      id: "ussd_plan_code",
    },
    "Bundle ID": {
      id: "bundle_id",
    },
    "Amount incl. Tax": {
      id: "amount_incl_tax",
      custom: ["amount_incl_tax"],
    },
    "Amount excl. Tax": {
      id: "amount_excl_tax",
      custom: ["amount_excl_tax"],
    },
    "Internal Subscription Fee": {
      id: "internal_subscription_fee",
      custom: ["internal_subscription_fee"],
    },
    "Plan Type": {
      id: "plan_types",
      custom: ["plan_types"],
    },
    "Plan Name": {
      id: "plan_name",
    },
    Action: {
      id: "actions",
      actionTypes: ["edit", "delete"],
    },
  };

  /**
   * Handles the row level actions of the listing.
   * The delete action passes the row index as `dataId`, so the plan id is read
   * from the row object instead.
   */
  const handleActions = async (dataId: string, key: string, row?: any) => {
    if (key === "edit") {
      navigation(
        `/mno-management/list-of-mnos/view-mno/${id}/bundle-plans/edit-plan/${row?.id ?? dataId}`
      );
    } else if (key === "delete") {
      setPlanToDelete(row ?? null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete?.id) return;
    setDeleteLoading(true);
    const response = await DeleteAPI(`${bundlePlans}/${planToDelete.id}`);
    setDeleteLoading(false);

    if (!response?.error) {
      setPlanToDelete(null);
      setResequencedCount(Number(response?.data?.data?.resequenced_plans_count) || 0);
      setIsDeletedModalOpen(true);
      // Deleting renumbers the MNO's USSD plan codes, so the list is refetched to
      // pick up the new ones rather than just dropping the deleted row.
      setFetchListTrigger((prev) => !prev);
    } else {
      setPlanToDelete(null);
      dispatch(
        showToast({
          message:
            response?.data?.response?.data?.message ||
            "Failed to delete the bundle plan. Please try again.",
          type: "error",
        })
      );
    }
  };

  const addButtonClick = () => {
    navigation(`/mno-management/list-of-mnos/view-mno/${id}/bundle-plans/create-plan`);
  };

  const staticText = {
    emptyScreenText: {
      heading1: translate("There are no bundle plans added yet"),
      heading2: translate("Click “Create Bundle Plan” to create one"),
    },
  };

  return (
    <div>
      <Breadcrumbs
        navPath={["MNO Management", "List of MNO’s", "View Details", "Bundle Plan List"]}
        heading="Bundle Plan List"
        noNavigation
        fullNavPath
        links={[
          pathname ? pathname : "/mno-management/list-of-mnos",
          pathname ? pathname : "/mno-management/list-of-mnos",
          `/mno-management/list-of-mnos/view-mno/${id}`,
          `/mno-management/list-of-mnos/view-mno/${id}/bundle-plans`,
        ]}
      />
      <MainTable
        staticText={staticText}
        mainEndPoint={bundlePlans}
        TableDetails={TableDetails}
        handleActions={handleActions}
        // The MNO is pinned for every request; the listing is always scoped to it.
        staticEndPoint={`mno_id=${id}`}
        // The endpoint validates its query schema strictly and rejects client_timezone.
        skipClientTimezone
        // Header inside the table card, separated from the table by a divider.
        TabChildren={
          <div className="px-6 py-5 border-b border-[#E5E9EB] flex flex-wrap gap-y-3 justify-between items-center">
            <div>
              <h2 className="text-heading2 text-primary_text">{translate("Bundle Plans")}</h2>
              <p className="text-body2 text-primary_text">
                {entireResponse?.mno_details?.name || ""}
              </p>
            </div>
            {bundlePlanPrivileges.add && (
              <Button
                buttonName={translate("Create Bundle Plan")}
                buttonOnclick={addButtonClick}
                buttonWidth="w-fit"
                className="px-4 whitespace-nowrap"
                textSize="text-large"
                buttonHight="h-[45px]"
                testId="create-bundle-plan"
              />
            )}
          </div>
        }
        // The table area sizes to its rows so the in-card header cannot push a row
        // out of view, while the card stretches to fill the remaining page height so
        // the blue gutter stays even on every side.
        cardHeightClass="min-h-[calc(100vh-223px)] h-auto"
        scrollHeightClass="h-auto"
        // Sized to its content with a floor, so the illustration and copy stay inside
        // the card at higher zoom levels instead of overflowing a fixed height.
        emptyStateHeightClass="min-h-[420px] h-auto py-10"
        params={{ page_number: "1" }}
        search={false}
        filters={false}
        exporting={false}
        fetchListTrigger={fetchListTrigger}
        hasListPrivilege={bundlePlanPrivileges.list}
        hasEditMDPrivilege={bundlePlanPrivileges.edit}
        notfoundRoute={{ state: { route: "/mno-management/list-of-mnos" } }}
      />
      {planToDelete && (
        <ConfirmDeletePlanModal
          loading={deleteLoading}
          onCancel={() => !deleteLoading && setPlanToDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
      {isDeletedModalOpen && (
        <DeleteSuccessModal
          resequencedCount={resequencedCount}
          onGoBack={() => setIsDeletedModalOpen(false)}
        />
      )}
    </div>
  );
}
